/**
 * Form validation utilities
 * Handles regex validation, required field checks, and custom validators
 */

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const phoneRegex = /^[\d\s\-\+\(\)]+$/;
export const nhsNumberRegex = /^\d{3}\s?\d{3}\s?\d{4}$/;

/**
 * Build Ant Form validation rules from formsData field config
 */
export const buildValidationRules = (field) => {
  const rules = [];

  // Required field validation
  if (field.is_required) {
    rules.push({
      required: true,
      message: `${field.label} is required`,
    });
  }

  // Type-specific validations
  switch (field.field_type) {
    case "email":
      rules.push({
        pattern: emailRegex,
        message: `${field.label} must be a valid email address`,
      });
      break;

    case "phone":
      rules.push({
        pattern: phoneRegex,
        message: `${field.label} must contain valid phone number characters`,
      });
      break;

    case "number":
      rules.push({
        type: "number",
        message: `${field.label} must be a number`,
      });
      break;

    default:
      break;
  }

  // Custom regex validation from field config
  if (field.validation_regex) {
    try {
      const regex = new RegExp(field.validation_regex);
      rules.push({
        pattern: regex,
        message: field.validation_message || `${field.label} format is invalid`,
      });
    } catch (e) {
      console.warn(
        `Invalid regex for field ${field.field_key}:`,
        field.validation_regex,
      );
    }
  }

  return rules;
};

/**
 * Compute field value from another field
 * E.g., calculate age from date_of_birth
 */
export const computeFieldValue = (fieldConfig, formValues) => {
  if (!fieldConfig.is_computed || !fieldConfig.computed_from) {
    return null;
  }

  const sourceValue = formValues[fieldConfig.computed_from];

  switch (fieldConfig.computed_from) {
    case "date_of_birth":
      if (!sourceValue) return null;
      const birthDate = new Date(sourceValue);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age;

    default:
      return null;
  }
};

/**
 * Validate entire form section
 */
export const validateSection = async (form, sectionId) => {
  try {
    const values = await form.validateFields();
    return { isValid: true, values };
  } catch (error) {
    //@ts-ignore
    return { isValid: false, errors: error.errorFields };
  }
};

/**
 * Format field value for display
 */
export const formatFieldValue = (value, fieldType) => {
  if (value === null || value === undefined) {
    return "";
  }

  switch (fieldType) {
    case "date":
      return value ? new Date(value).toLocaleDateString("en-GB") : "";

    case "phone":
      // Format as XXX XXXX XXXX if looks like UK number
      const cleaned = String(value).replace(/\D/g, "");
      if (cleaned.length === 10) {
        return cleaned.replace(/(\d{3})(\d{4})(\d{3})/, "$1 $2 $3");
      }
      return value;

    case "boolean":
      return value ? "Yes" : "No";

    default:
      return String(value);
  }
};

/**
 * Check if field should be visible based on conditions
 */
export const isFieldVisible = (field, formValues) => {
  if (!field.condition_field_key || !field.condition_field_value) {
    return true;
  }

  const fieldValue = formValues[field.condition_field_key];
  return fieldValue === field.condition_field_value;
};

/**
 * Flatten section fields with computed values
 */
export const getSectionFields = (section, formValues = {}) => {
  const items = section.fields?.items || [];

  return items.map((field) => {
    // Compute values if needed
    if (field.is_computed && formValues[field.computed_from]) {
      return {
        ...field,
        computed_value: computeFieldValue(field, formValues),
      };
    }

    return field;
  });
};

/**
 * Extract section data from form values
 */
export const extractSectionData = (section, formValues = {}) => {
  const data = {};
  const fields = section.fields?.items || [];

  fields.forEach((field) => {
    if (formValues[field.field_key] !== undefined) {
      data[field.field_key] = formValues[field.field_key];
    }
  });

  return data;
};
